<script lang="ts" setup>
import { computed, navigateTo, onMounted, ref, showErrorMsg } from '#imports';
import { declareProviders, useService } from '@kaokei/use-vue-service';
import ArrowBtnIcon from '~/assets/image/index/arrow-btn.svg';
import questionCircleImg from '~/assets/image/index/question-circle.svg';
import titleImg from '~/assets/image/index/title/mqxg.svg';
import BadgeWall from '~/components/BadgeWall/BadgeWall.vue';
import BadgeWrapper from '~/components/BadgeWall/BadgeWrapper.vue';
import GoodsResultModal from '~/components/GoodsResultModal/GoodsResultModal.vue';
import HTMdPreview from '~/components/HTMdPreview.vue';
import { UserService } from '~/services/user.service';
import type { DrawGoodsVo } from '~/types/auto/actuator';
import { timeRemain } from '~/utils/dayjs';
import ModuleTitle from '../ModuleTitle.vue';
import Confirm from './Confirm.vue';
import { WeeklyGoodsService } from './WeeklyGoods.service';

declareProviders([WeeklyGoodsService]);
const service = useService(WeeklyGoodsService);
const userService = useService(UserService);

type ConfirmType = 'not-enough-goods';

interface ConfirmParams {
  type: string;
  title: string;
  content: string;
  okText: string;
  cancelText: string;
  onOk: () => void;
  onCancel: () => void;
  warning: boolean;
}

// src/components/WeeklyGoods/constants.ts

const BASE_CONFIG: Omit<ConfirmParams, 'onOk' | 'onCancel'> = {
  type: '',
  title: '',
  content: '',
  okText: '',
  cancelText: '取消',
  warning: false,
};

const CONFIRM_CONFIGS: Record<ConfirmType, Partial<ConfirmParams>> = {
  'not-enough-goods': {
    type: 'not-enough-goods',
    title: '抽谷机会不足啦',
    content: '快去刷题换取机会吧～',
    okText: '去刷题',
    warning: true,
  },
};

// 响应式状态
const badgeWallVisible = ref(false);
const confirmVisible = ref(false);
const drawResultModalInfo = ref({
  visible: false,
  drawCount: 0,
});
const drawResult = ref<DrawGoodsVo>({});

const confirmParams = ref<ConfirmParams>({
  ...BASE_CONFIG,
  onOk: () => {},
  onCancel: () => {},
});

// 计算属性
const goodsInfo = computed(() => service.goodsInfo);
const remainingCount = computed(() => goodsInfo.value?.totalCount ?? 0);
const totalLimit = computed(() => goodsInfo.value?.totalCountLimit ?? 0);
const isGoodsEnd = computed(() => {
  if (goodsInfo.value?.endTime) {
    const endTime = goodsInfo.value.endTime;
    const now = new Date().getTime();
    return endTime <= now;
  }
  return true;
});
const remainTime = computed(() => {
  if (goodsInfo.value?.endTime) {
    const endTime = goodsInfo.value.endTime;
    const now = new Date().getTime();
    if (endTime > now) {
      return timeRemain(endTime);
    } else {
      return '本期已结束';
    }
  }
  return '';
});
const ruleLink = computed(() => {
  const info = service.ruleInfo;
  if (info.posterId) {
    return {
      name: 'discuss_post_detail',
      query: {
        id: info.posterId,
        type: 1,
      },
    };
  }
  return '';
});

const showGoodsResultModal = (count: number) => {
  drawResultModalInfo.value = {
    visible: true,
    drawCount: count,
  };
};
const hideGoodsResultModal = () => {
  drawResultModalInfo.value = {
    visible: false,
    drawCount: 0,
  };
};

// 核心抽奖逻辑
const performDraw = async (
  drawType: 'single' | 'triple',
  count: number,
  requestFn: () => Promise<DrawGoodsVo>,
) => {
  if (remainingCount.value < count) {
    showConfirm('not-enough-goods');
    return;
  }
  try {
    drawResultModalInfo.value = {
      visible: true,
      drawCount: count,
    };
    confirmVisible.value = false;
    service.setLoading(drawType, true);
    service.updateCount(-count);

    const result = await requestFn();
    handleDrawResult(result);
  } catch (err) {
    handleDrawError(err as Error, count);
  } finally {
    service.setLoading(drawType, false);
  }
};

const performDrawSingle = () => {
  performDraw('single', 1, service.drawSingleGoods);
};

const performDrawTriple = () => {
  performDraw('triple', 5, service.draw3TimesGoods);
};
const handleDrawResult = (result: DrawGoodsVo) => {
  drawResult.value = result;
  service.getGoodsByWeek({ withLoading: false });
};

const handleDrawError = (err: Error, count: number) => {
  showErrorMsg(err);
  service.updateCount(count);
  hideGoodsResultModal();
  console.error('抽奖失败:', err);
};

// 确认框管理
const showConfirm = (type: ConfirmType) => {
  confirmParams.value = {
    ...BASE_CONFIG,
    ...CONFIRM_CONFIGS[type],
    onOk: getConfirmHandler(type),
    onCancel: () => (confirmVisible.value = false),
  };
  confirmVisible.value = true;
};

const getConfirmHandler = (type: ConfirmType) => {
  switch (type) {
    case 'not-enough-goods':
      return goToProblemPage;
    default:
      return () => performDraw('single', 1, service.drawSingleGoods);
  }
};

// 页面跳转
const goToProblemPage = () => {
  navigateTo({ name: 'oj_problem' });
  confirmVisible.value = false;
};

const ACTIVITY = {
  NORMAL: 1,
  DRAGON_BOAT: 2,
};
const isLimit = computed(
  () => service.goodsInfo?.isSpecial === ACTIVITY.NORMAL,
);
const chanceName = computed(() => {
  if (service.goodsInfo?.isSpecial === ACTIVITY.NORMAL) {
    return '抽谷机会';
  }
  return '粽子';
});
// 生命周期
onMounted(() => service.init());
</script>

<template>
  <div
    v-if="goodsInfo"
    v-track:newoj_goodsbyweek_show
    class="weekly-goods rounded border border-lightblack bg-white p-3 text-sm text-colorTextSecondary"
  >
    <!-- 标题部分 -->
    <div class="flex h-[18px] items-center">
      <ModuleTitle class="w-full">
        <img class="h-[18px]" :src="titleImg" alt="每期新谷" />
        <a-tooltip
          color="#fff"
          overlay-class-name="rule-info-tooltip"
          placement="bottom"
          trigger="click"
        >
          <template #title>
            <div>
              <div class="mb-2 flex items-center justify-between">
                <h5 class="font-bold text-colorText">规则说明</h5>
                <NuxtLink
                  class="flex items-center"
                  :to="ruleLink"
                  target="_blank"
                >
                  <span class="text-xs text-font2">查看详情</span>
                  <img class="ml-1 w-4" :src="ArrowBtnIcon" />
                </NuxtLink>
              </div>
              <HTMdPreview
                :model-value="service.ruleInfo.posterContent"
              ></HTMdPreview>
            </div>
          </template>
          <img
            class="ml-1 w-4 cursor-pointer"
            :src="questionCircleImg"
            alt=""
          />
        </a-tooltip>
        <template #tip>
          <span class="ml-auto text-xs">{{ remainTime }}</span>
        </template>
      </ModuleTitle>
    </div>

    <!-- 分割线 -->
    <a-divider class="!my-5">
      <span
        class="mx-[-8px] block max-w-[228px] overflow-hidden text-ellipsis whitespace-nowrap text-xs font-bold leading-[22px] text-title"
      >
        {{ goodsInfo.name }}
      </span>
    </a-divider>

    <!-- 商品列表 -->
    <ul class="medal-item grid grid-cols-4 gap-x-4 gap-y-5 px-1.5">
      <li
        v-for="item in goodsInfo.goodsList"
        :key="item.id"
        class="relative h-14 w-14"
      >
        <a-popover :title="null" placement="bottom">
          <template #content>
            <div class="px-4 pb-2 pt-3">
              <div class="w-[140px]">
                <BadgeWrapper
                  :image="item.url"
                  :video="item.video"
                  :get="item.get"
                ></BadgeWrapper>
                <div class="mt-3 truncate text-center font-bold text-colorText">
                  {{ item.name }}
                </div>
              </div>
            </div>
          </template>
          <BadgeWrapper
            :image="item.url"
            :video="item.video"
            :get="item.get"
          ></BadgeWrapper>
        </a-popover>
      </li>
    </ul>

    <!-- 分割线 -->
    <a-divider class="!mb-3 !mt-6"></a-divider>

    <!-- 谷仓信息 -->
    <div class="mb-4 flex h-[22px] flex-wrap items-center">
      <div>
        <span class="text-xs">{{ chanceName }}</span>
        <span class="ml-1 font-bold text-xs text-font1">{{ remainingCount }}</span>
        <span
          v-if="service.goodsInfo?.isSpecial === ACTIVITY.NORMAL"
          class="text-xs text-colorTextTertiary"
          >（上限{{ totalLimit }}）</span
        >
        <span
          v-if="service.goodsInfo?.isSpecial === ACTIVITY.DRAGON_BOAT"
          class="text-xs leading-7 text-colorTextQuaternary"
        >
          (抽谷机会在活动后返还)
        </span>
      </div>
      <div
        v-login
        class="ml-auto flex cursor-pointer items-center text-font3"
        @click="badgeWallVisible = true"
      >
        <span class="text-xs text-font2">我的谷子</span>
        <img class="ml-1 w-4" :src="ArrowBtnIcon" />
      </div>
    </div>

    <!-- 操作按钮 -->
    <div class="pb-2">
      <div class="flex h-8 gap-6 flex-center">
        <a-button
          v-login
          v-track:newoj_goodsbyweek_draw_single.click
          :loading="service.loading.single"
          :disabled="service.loading.triple || isGoodsEnd"
          class="!font-bold"
          @click="performDrawSingle"
        >
          {{ service.loading.single ? '抽取中...' : '奇迹单抽 X1' }}
        </a-button>
        <a-button
          v-login
          v-track:newoj_goodsbyweek_draw_multi.click
          :loading="service.loading.triple"
          :disabled="service.loading.single || isGoodsEnd"
          type="primary"
          class="!font-bold"
          @click="performDrawTriple"
        >
          {{ service.loading.triple ? '抽取中...' : '素质五连 X5' }}
        </a-button>
      </div>
    </div>

    <!-- 模态窗口 -->
    <Confirm v-bind="confirmParams" v-model:open="confirmVisible" />
    <BadgeWall v-model:open="badgeWallVisible" :uid="userService.user.uid" />
    <GoodsResultModal
      v-model:open="drawResultModalInfo.visible"
      :draw-count="drawResultModalInfo.drawCount"
      :result="drawResult"
      :draw-single="performDrawSingle"
      :draw-multiple="performDrawTriple"
      :is-limit="service.goodsInfo?.isSpecial === ACTIVITY.NORMAL"
    />
  </div>
</template>

<style>
.rule-info-tooltip {
  max-width: 330px !important;
}
.rule-info-tooltip .ant-tooltip-inner {
  padding: 16px !important;
}
.rule-info-tooltip .md-editor-preview-wrapper {
  padding: 0 !important;
}
.rule-info-tooltip .md-editor-preview-wrapper .md-editor-preview p {
  font-size: 12px !important;
  line-height: 20px !important;
  @apply text-colorTextTertiary;
}
.rule-info-tooltip .md-editor-preview-wrapper .md-editor-preview p strong {
  @apply text-colorTextSecondary;
}
</style>
